// Cart service for API calls
import {
    CalendarCustomization,
    BusinessCardCustomization,
    CartItem,
    ItemPersonalization,
    Product,
    RawCartItem,
    RawLocationData,
    RawCustomization,
    ServiceResult,
    ProductVariation
} from '../types';
import {ProductService} from './productService';
import {fetchWithErrorHandling, guestPrefix} from './apiUtils';
import {ApiError} from '../errors/ApiError';

// Transform raw location data to our LocationData format
function transformLocationData(rawLocation: RawLocationData, defaultImage = 'calendar-1') {
  return {
    street: rawLocation.address || '',
    city: rawLocation.city || '',
    state: rawLocation.state || '',
    zip: rawLocation.zip_code || '',
    agency_combine: rawLocation.agency_combine || '',
    additional_agents: rawLocation.additional_agents || '',
    phone: rawLocation.phone || '',
    cell_phone: rawLocation.cell_phone || '',
    other_info: rawLocation.other_info || '',
    calendarImage: defaultImage,
    fontFamily: 'Inter, sans-serif',
    textColor: '#000000',
    formScale: 0.5,
    formOpacity: 0.2,
  };
}

// Transform raw customization to BusinessCardCustomization
function transformBusinessCardCustomization(rawCustomization: RawCustomization | null): BusinessCardCustomization | undefined {
  if (!rawCustomization || !rawCustomization.fields) {
    return undefined;
  }

  return { ...rawCustomization.fields };
}

// Transform raw customization to CalendarCustomization
function transformCustomization(rawCustomization: RawCustomization | null): CalendarCustomization | undefined {
  if (!rawCustomization || !rawCustomization.fields) {
    return undefined;
  }

  const defaultPosition = { x: 0, y: 0 };

  const customization: CalendarCustomization = {
    locations: {
      primary: {
        ...transformLocationData(rawCustomization.fields),
        formPosition: defaultPosition,
      },
    },
  };

  if (rawCustomization.second_location) {
    customization.locations.secondary = {
      ...transformLocationData(rawCustomization.second_location),
      formPosition: defaultPosition,
    };
  }

  if (rawCustomization.third_location) {
    customization.locations.tertiary = {
      ...transformLocationData(rawCustomization.third_location),
      formPosition: defaultPosition,
    };
  }

  return customization;
}

// Transform raw cart item to CartItem
// Note: Backend provides all necessary product data, so we don't need to look it up
async function transformRawCartItem(rawItem: RawCartItem, products: Product[]): Promise<CartItem | null> {
  // Try to find the full product data if available (for additional info like multiple images)
  const fullProduct = products.find(p => p.id === rawItem.productId);

  // Construct a minimal Product object from cart data
  // The backend provides name, image, and price already
  const product: Product = fullProduct || {
    id: rawItem.productId,
    name: rawItem.name,
    description: '',
    basePrice: rawItem.price,
    images: [rawItem.image],
    variations: [],
    isPrintDetail: rawItem.isPrintDetail,
    allowBackorder: false,
    allowOnDemand: false,
    departments: [],
    categories: [],
    productionTimeline: rawItem.productionTimeline,
  };

  // Find matching variation from full product data if available
  let variation: ProductVariation;

  if (fullProduct && fullProduct.variations && fullProduct.variations.length > 0) {
    // Try to find exact match by color and size
    let matchedVariation = fullProduct.variations.find(v => {
      const colorMatch = !rawItem.color || v.color === rawItem.color;
      const sizeMatch = !rawItem.size || v.size === rawItem.size;
      return colorMatch && sizeMatch && v.color === rawItem.color && v.size === rawItem.size;
    });

    // If no exact match, try color-only match
    if (!matchedVariation && rawItem.color && !rawItem.size) {
      matchedVariation = fullProduct.variations.find(v => v.color === rawItem.color);
    }

    // If no exact match, try size-only match
    if (!matchedVariation && rawItem.size && !rawItem.color) {
      matchedVariation = fullProduct.variations.find(v => v.size === rawItem.size);
    }

    // If still no match, use first variation
    if (!matchedVariation) {
      matchedVariation = fullProduct.variations[0];
    }

    variation = matchedVariation;
  } else {
    // Construct a minimal ProductVariation object when no product data available
    variation = {
      id: `${rawItem.productId}-${rawItem.color || 'default'}-${rawItem.size || 'default'}`,
      color: rawItem.color || 'Default',
      size: rawItem.size || 'Standard',
      price: rawItem.price,
      stock: 999, // Unknown from cart data, assume available
      images: [rawItem.image],
    };
  }

  // Transform customization based on product print type
  const printDetailType = fullProduct?.printDetailType;
  const calendarCustomization = printDetailType === 'calendar'
    ? transformCustomization(rawItem.customization)
    : undefined;
  const businessCardCustomization = printDetailType === 'business_card'
    ? transformBusinessCardCustomization(rawItem.customization)
    : undefined;

  // Transform personalizations array if present
  const personalizations: ItemPersonalization[] | undefined =
    rawItem.personalizations?.length
      ? rawItem.personalizations.map(p => ({
          text: p.text,
          placement: p.placement,
          fee: rawItem.personalization_fee ?? undefined,
        }))
      : undefined;

  return {
    id: rawItem.id,
    shoppingCartId: rawItem.shoppingCartId,
    productId: rawItem.productId,
    variationId: String(variation.id),
    quantity: rawItem.quantity,
    product,
    variation,
    calendarCustomization,
    businessCardCustomization,
    personalizations,
    logoOptionName: rawItem.logo_option_name ?? null,
  };
}

export class CartService {
  static async getCart(): Promise<CartItem[]> {
    try {
      // Try API first - use guest or authenticated endpoint based on auth state
      try {
        const endpoint = `/api/v2${guestPrefix()}/cart/items`;

        const rawCartItems = await fetchWithErrorHandling<RawCartItem[]>(endpoint);

        // Fetch all products to match with cart items
        const productsResponse = await ProductService.getProducts();

        // Transform raw cart items to CartItems
        const cartItemPromises = rawCartItems.map(rawItem =>
          transformRawCartItem(rawItem, productsResponse.data)
        );

        const cartItems = await Promise.all(cartItemPromises);

        // Filter out any null items (where product wasn't found)
        return cartItems.filter((item): item is CartItem => item !== null);
      } catch (apiError) {
        console.warn('API not available, falling back to local data:', apiError);

        // Fall back to local JSON file
        try {
          const { default: localCartData } = await import('../data/cart.sample.data.json');
          const productsResponse = await ProductService.getProducts();

          const cartItemPromises = (localCartData as RawCartItem[]).map(rawItem =>
            transformRawCartItem(rawItem, productsResponse.data)
          );

          const cartItems = await Promise.all(cartItemPromises);
          return cartItems.filter((item): item is CartItem => item !== null);
        } catch (localError) {
          console.error('Failed to load local cart data:', localError);
          throw new Error('Unable to load cart from both API and local data.');
        }
      }
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      throw new Error('Unable to load cart. Please try again later.');
    }
  }

  static async addToCart(
    productId: number,
    variationId: string,
    quantity: number,
    customization?: CalendarCustomization,
    color?: string,
    size?: string,
    personalizations?: ItemPersonalization[],
    businessCardCustomization?: BusinessCardCustomization,
    logoOptionId?: number | null,
  ): Promise<ServiceResult> {
    try {
      const endpoint = `/api/v2${guestPrefix()}/cart/items`;

      await fetchWithErrorHandling(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          product_id: productId,
          variation_id: variationId,
          quantity,
          color,
          size,
          customization: customization
            ? JSON.stringify(this.transformCustomizationToAPI(customization))
            : businessCardCustomization
              ? JSON.stringify(this.transformBusinessCardCustomizationToAPI(businessCardCustomization))
              : null,
          personalizations: personalizations?.length
            ? personalizations.map(p => ({ text: p.text, placement: p.placement }))
            : null,
          logo_option_id: logoOptionId ?? null,
        }),
      }, { handle422Errors: true });

      return { success: true };
    } catch (error: unknown) {
      // Handle Laravel 422 validation errors
      if (error instanceof ApiError && error.statusCode === 422) {
        return { success: false, warningMessage: error.message };
      }

      console.error('Failed to add item to cart:', error);
      throw new Error('Unable to add item to cart. Please try again later.');
    }
  }

  static async updateCartItemQuantity(itemId: string, quantity: number): Promise<ServiceResult> {
    try {
      const endpoint = `/api/v2${guestPrefix()}/cart/items/${itemId}/quantity`;

      await fetchWithErrorHandling(endpoint, {
        method: 'POST',
        body: JSON.stringify({ quantity }),
      }, { handle422Errors: true });

      return { success: true };
    } catch (error: unknown) {
      // Handle Laravel 422 validation errors
      if (error instanceof ApiError && error.statusCode === 422) {
        return { success: false, warningMessage: error.message };
      }

      console.error('Failed to update cart item quantity:', error);
      throw new Error('Unable to update cart item quantity. Please try again later.');
    }
  }

  static async removeFromCart(itemId: string): Promise<ServiceResult> {
    try {
      const endpoint = `/api/v2${guestPrefix()}/cart/items/${itemId}`;

      await fetchWithErrorHandling(endpoint, {
        method: 'DELETE',
      }, { handle422Errors: true });

      return { success: true };
    } catch (error: unknown) {
      // Handle Laravel 422 validation errors
      if (error instanceof ApiError && error.statusCode === 422) {
        return { success: false, warningMessage: error.message };
      }

      console.error('Failed to remove item from cart:', error);
      throw new Error('Unable to remove item from cart. Please try again later.');
    }
  }

    static async clearCart(): Promise<ServiceResult> {
        try {
            const endpoint = `/api/v2${guestPrefix()}/cart`;

            await fetchWithErrorHandling(endpoint, {
                method: 'DELETE',
            });

            return {success: true};
        } catch (error) {
            console.error('Failed to clear cart:', error);
            throw new Error('Unable to clear cart. Please try again later.');
        }
    }


    // Helper method to transform CalendarCustomization back to API format
    private static transformCustomizationToAPI(customization: CalendarCustomization): RawCustomization {
        const result: RawCustomization = {
            fields: {
                address: customization.locations.primary.street,
                city: customization.locations.primary.city,
                state: customization.locations.primary.state,
                zip_code: customization.locations.primary.zip,
                agency_combine: customization.locations.primary.agency_combine,
                additional_agents: customization.locations.primary.additional_agents,
                phone: customization.locations.primary.phone,
                cell_phone: customization.locations.primary.cell_phone,
                other_info: customization.locations.primary.other_info,
            },
        };

    if (customization.locations.secondary) {
      result.second_location = {
        address: customization.locations.secondary.street,
        city: customization.locations.secondary.city,
        state: customization.locations.secondary.state,
        zip_code: customization.locations.secondary.zip,
        agency_combine: customization.locations.secondary.agency_combine,
        additional_agents: customization.locations.secondary.additional_agents,
        phone: customization.locations.secondary.phone,
        cell_phone: customization.locations.secondary.cell_phone,
        other_info: customization.locations.secondary.other_info,
      };
    }

    if (customization.locations.tertiary) {
      result.third_location = {
        address: customization.locations.tertiary.street,
        city: customization.locations.tertiary.city,
        state: customization.locations.tertiary.state,
        zip_code: customization.locations.tertiary.zip,
        agency_combine: customization.locations.tertiary.agency_combine,
        additional_agents: customization.locations.tertiary.additional_agents,
        phone: customization.locations.tertiary.phone,
        cell_phone: customization.locations.tertiary.cell_phone,
        other_info: customization.locations.tertiary.other_info,
      };
    }

    return result;
  }

  private static transformBusinessCardCustomizationToAPI(customization: BusinessCardCustomization): object {
    return { fields: { ...customization } };
  }
}
